import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, CustomDomainStatus, DeploymentStatus } from '@back4app2/sdk';
import back4app2 from '../back4app2';
import Modal from './Modal';
import { DsButton, Eyebrow, StatusDot } from './ds';
import { CloseIcon, ExternalLinkIcon } from './ds/icons';
import { upgradePath } from '../containers/MyAppSettings';

/**
 * The moment the first deploy lands.
 *
 * Until now the only sign a new app had gone live was a status dot turning
 * green somewhere on the Overview. This says it once, out loud, and hands over
 * the one thing the customer came for — the address.
 *
 * On a free plan the address is rented (`TemporaryUrlNotice` has the term), so
 * the same dialog carries the offer of a permanent one. That is the moment the
 * offer is worth most: the app works, and the URL they are about to share is
 * the part that will stop working.
 *
 * Once per app, and the server is what keeps it to once. The dialog opens only
 * when `claimFirstDeployCelebration` answers true, and the server answers true
 * a single time — it stamps the app in a conditional UPDATE and also owns the
 * "first" test (no earlier deployment ever went live). A second tab, another
 * browser or a reload all get false.
 *
 * Owner only: a collaborator's visit must not spend the showing.
 */

/** Deployment states that mean the build ran and the container came up. */
const LIVE = new Set<DeploymentStatus>([
  DeploymentStatus.READY,
  DeploymentStatus.SLEEPY,
  DeploymentStatus.SLEEPING,
  DeploymentStatus.WAKING_UP,
]);

/**
 * A short fall of paper from the top edge. Positions come from the index, not
 * Math.random, so a re-render cannot reshuffle pieces that are mid-flight.
 */
const CONFETTI_COLORS = ['bg-state-ok', 'bg-action-soft', 'bg-state-warn', 'bg-state-ok-soft', 'bg-navy-100'];
const CONFETTI = Array.from({ length: 22 }, (_, i) => ({
  left: (i * 37 + 11) % 100,
  delay: (i * 53) % 320,
  duration: 1300 + ((i * 97) % 700),
  drift: ((i * 29) % 60) - 30,
  spin: ((i % 2 === 0 ? 1 : -1) * (180 + ((i * 41) % 360))),
  wide: i % 3 === 0,
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
}));

const Confetti = () => (
  <div aria-hidden="true" className="b4-confetti pointer-events-none absolute inset-x-0 top-0 h-48 overflow-hidden">
    {CONFETTI.map((piece, i) => (
      <span
        key={i}
        className={`b4-confetti-piece absolute top-0 rounded-[1px] ${piece.color} ${piece.wide ? 'w-2 h-1' : 'w-1 h-2'}`}
        style={{
          left: `${piece.left}%`,
          animationDelay: `${piece.delay}ms`,
          animationDuration: `${piece.duration}ms`,
          ['--b4-drift' as string]: `${piece.drift}px`,
          ['--b4-spin' as string]: `${piece.spin}deg`,
        }}
      />
    ))}
  </div>
);

/**
 * Whether the tab is on screen. A tab left open in the background still gets
 * every snapshot, and without this it would spend the app's one showing — and
 * run the dialog's auto-close — with nobody looking at it.
 */
const usePageVisible = () => {
  const [isVisible, setIsVisible] = useState(() => document.visibilityState === 'visible');

  useEffect(() => {
    const onChange = () => setIsVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  return isVisible;
};

const FirstDeployModal = ({ app }: { app: App }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isPageVisible = usePageVisible();
  const close = useCallback(() => setIsOpen(false), []);
  /** The deployment a claim was already sent for, so snapshot re-renders do not resend it. */
  const claimedFor = useRef<string | undefined>(undefined);

  const env = app.mainService?.mainServiceEnvironment;
  const deployment = env?.lastDeployment;
  const domain = env?.mainCustomDomain;
  const isFreePlan = !!env?.plan?.name?.includes('Free');

  useEffect(() => {
    // Wait for the address as well as the container: a dialog that says "live"
    // beside a URL that does not answer yet is the one thing this cannot do.
    if (
      !isPageVisible
      || !app.isOwner
      || app.firstDeployCelebrated !== false
      || app.block
      || !deployment?.id
      || !LIVE.has(deployment.status)
      || domain?.status !== CustomDomainStatus.READY
      || claimedFor.current === deployment.id
    ) {
      return;
    }

    claimedFor.current = deployment.id;

    // No cancellation on purpose. The claim spends the one showing on the
    // server; an answer thrown away because React re-ran the effect would
    // spend it without the dialog ever opening.
    back4app2.claimFirstDeployCelebration(app.id)
      .then(isFirst => {
        if (isFirst) {
          setIsOpen(true);
        }
      })
      .catch(error => {
        // Nothing was stamped, so the next snapshot may try again.
        claimedFor.current = undefined;
        console.error('could not claim the first deploy dialog', error);
      });
  }, [isPageVisible, app.id, app.isOwner, app.firstDeployCelebrated, app.block, deployment?.id, deployment?.status, domain?.status]);

  if (!isOpen || !domain) {
    return null;
  }

  return (
    <DeployLiveDialog
      app={app}
      domainName={domain.name}
      expiresAt={isFreePlan && domain.expiresAt ? new Date(domain.expiresAt) : undefined}
      isFreePlan={isFreePlan}
      planName={env?.plan?.name}
      isPageVisible={isPageVisible}
      onClose={close}
    />
  );
};

/** How long the dialog stays up on its own before it gets out of the way. */
const AUTO_CLOSE_SECONDS = 30;
/** Below this the countdown turns amber: the address is about to go. */
const URGENT_SECONDS = 5 * 60;

const clockTime = (date: Date) =>
  date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

/** m:ss — the clock never needs hours, the free term is an hour at most. */
const countdown = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/**
 * The dialog itself, mounted only while open so its timers live and die with it.
 *
 * Read top to bottom it answers three questions in order: did it work (the
 * eyebrow, the title), what is the plan doing to the address (the line under
 * the title and, on Free, the clock beside it), and where is it (the URL well).
 * The footer then offers the two ways out, with the auto-close note on the
 * left so the dialog never has to be dismissed to get back to work.
 */
const DeployLiveDialog = ({ app, domainName, expiresAt, isFreePlan, planName, isPageVisible, onClose }: {
  app: App,
  domainName: string,
  /** Free plan only: when the sweeper takes the address. */
  expiresAt?: Date,
  isFreePlan: boolean,
  planName?: string,
  /** The auto-close only counts down while someone can see it count. */
  isPageVisible: boolean,
  onClose: () => void,
}) => {
  const navigate = useNavigate();
  const [now, setNow] = useState(() => Date.now());
  const [isCopied, setIsCopied] = useState(false);
  const [autoCloseLeft, setAutoCloseLeft] = useState<number | null>(AUTO_CLOSE_SECONDS);
  const [isAutoClosePaused, setIsAutoClosePaused] = useState(false);
  const [upgradePrice, setUpgradePrice] = useState<number | undefined>(undefined);

  // One tick drives both the URL clock and the auto-close.
  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, []);

  useEffect(() => {
    if (autoCloseLeft === null || isAutoClosePaused || !isPageVisible) {
      return;
    }
    if (autoCloseLeft <= 0) {
      onClose();
      return;
    }
    const timeout = window.setTimeout(() => setAutoCloseLeft(left => (left === null ? null : left - 1)), 1000);
    return () => window.clearTimeout(timeout);
  }, [autoCloseLeft, isAutoClosePaused, isPageVisible, onClose]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // The button names the price it leads to. The cheapest paid plan is the
  // one step up from Free; until it loads the button just says Upgrade.
  useEffect(() => {
    if (!isFreePlan) {
      return;
    }
    let isCancelled = false;
    back4app2.getPlans()
      .then(plans => {
        const cheapest = plans
          .filter(plan => plan.price > 0)
          .sort((a, b) => a.price - b.price)[0];
        if (!isCancelled && cheapest) {
          setUpgradePrice(cheapest.price);
        }
      })
      .catch(error => console.error('could not load plans for the upgrade price', error));
    return () => {
      isCancelled = true;
    };
  }, [isFreePlan]);

  const href = `${window.location.protocol}//${domainName}`;
  const secondsLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt.getTime() - now) / 1000)) : undefined;
  const isExpired = secondsLeft === 0;
  const isUrgent = secondsLeft !== undefined && secondsLeft <= URGENT_SECONDS;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(href);
      setIsCopied(true);
      window.setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // Clipboard refused (permissions, insecure origin): the address is on
      // screen to select by hand.
    }
  };

  const openApp = () => window.open(href, '_blank', 'noopener,noreferrer');

  // Any deliberate move — hovering, tabbing into it — means the customer is
  // reading, and a dialog that closes under the pointer is worse than none.
  const pauseAutoClose = () => setIsAutoClosePaused(true);
  const resumeAutoClose = () => setIsAutoClosePaused(false);

  // The Free term starts when the address is created, at the start of the
  // deploy — not when it goes live — so the clock opens below 60:00 by however
  // long the build took. Saying where the hour is counted from is what keeps
  // a 57:54 from reading as a mistake.
  const planLine = isFreePlan
    ? 'Running on the Free Plan, which keeps this URL live for 60 min from the start of the deploy.'
    : `You are on ${planName ?? 'a paid plan'}, so this URL is permanent.`;

  return (
    <Modal open onClose={onClose} theme="CONTAINER" showCloseIcon={false} minWidth="620px">
      <div
        className="relative flex flex-col gap-8 py-2"
        onMouseEnter={pauseAutoClose}
        onMouseLeave={resumeAutoClose}
        onFocus={pauseAutoClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="first-deploy-title"
      >
        <Confetti />

        <div className="relative flex flex-col gap-5">
          <div className="b4-party-pop relative flex items-center gap-2.5 min-h-[12px]">
            <StatusDot tone="ok" halo />
            <Eyebrow>first deploy</Eyebrow>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="b4-focus absolute top-1/2 -right-2 -translate-y-1/2 w-8 h-8 rounded-control flex items-center justify-center text-navy-200 hover:text-white hover:bg-navy-600 transition-colors duration-[120ms]"
            >
              <CloseIcon size={16} />
            </button>
          </div>

          <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-5">
            <div className="flex flex-col gap-2.5 flex-[1_1_260px] min-w-0">
              <h2 id="first-deploy-title" className="m-0 font-sora text-ds-20 font-semibold tracking-[-0.02em] text-white">
                {isExpired ? 'The temporary URL expired' : 'Your app is ready!'}
              </h2>
              <p className="m-0 max-w-[42ch] text-ds-135 leading-[1.55] text-navy-200">
                {planLine}
              </p>
            </div>

            {secondsLeft !== undefined && expiresAt ? (
              <div className="flex-none flex flex-col items-end gap-[7px]">
                <Eyebrow>{isExpired ? 'expired' : 'url expires in'}</Eyebrow>
                <span
                  className={`font-sora text-ds-36 font-semibold tracking-[-0.02em] leading-none tabular-nums ${isExpired ? 'text-navy-400' : isUrgent ? 'text-state-warn' : 'text-white'}`}
                  aria-live="off"
                >
                  {isExpired ? '—' : countdown(secondsLeft)}
                </span>
                <span className="font-courrier text-ds-115 text-navy-300">
                  {isExpired ? `ended ${clockTime(expiresAt)}` : `ends ${clockTime(expiresAt)}`}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        <div className="relative flex flex-col gap-2">
          <Eyebrow>{isFreePlan ? 'temporary url' : 'app url'}</Eyebrow>
          <div className="flex items-center gap-2 min-h-[48px] pl-4 pr-2 rounded-control border border-navy-400 bg-navy-950 min-w-0">
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              title={href}
              className={`b4-focus flex-1 min-w-0 truncate font-courrier text-ds-135 ${isExpired ? 'text-navy-400 pointer-events-none' : 'text-state-ok-soft hover:text-white'}`}
            >
              {domainName}
            </a>
            <button
              type="button"
              onClick={copy}
              aria-label={isCopied ? 'Copied' : 'Copy URL'}
              className="b4-focus flex-none min-w-[52px] h-10 px-2 rounded-[4px] flex items-center justify-center font-courrier text-ds-115 text-navy-100 hover:text-white hover:bg-navy-700 transition-colors duration-[120ms]"
            >
              {isCopied ? 'Copied' : 'Copy'}
            </button>
            <button
              type="button"
              onClick={openApp}
              aria-label="Open in a new tab"
              disabled={isExpired}
              className="b4-focus flex-none min-w-[44px] h-10 rounded-[4px] flex items-center justify-center text-navy-100 hover:text-white hover:bg-navy-700 disabled:text-navy-400 disabled:hover:bg-transparent transition-colors duration-[120ms]"
            >
              <ExternalLinkIcon size={16} />
            </button>
          </div>
          {!isFreePlan ? (
            <span className="text-ds-115 leading-[1.5] text-navy-200">Add a custom domain in Settings.</span>
          ) : isExpired ? (
            <span className="text-ds-115 leading-[1.5] text-state-warn">No longer answering.</span>
          ) : null}
        </div>

        <div className="relative flex flex-wrap items-center gap-4 pt-6 border-t border-navy-700">
          {autoCloseLeft !== null && autoCloseLeft > 0 ? (
            <span className="font-courrier text-ds-115 text-navy-300">
              {isAutoClosePaused ? 'Auto-close paused' : `Closes in ${autoCloseLeft}s`}
            </span>
          ) : null}
          <div className="ml-auto flex-none flex items-center gap-2">
            <DsButton variant="ghost" onClick={onClose}>
              {isFreePlan ? 'Maybe later' : 'Close'}
            </DsButton>
            {isFreePlan ? (
              <DsButton
                variant="primary"
                onClick={() => {
                  onClose();
                  navigate(upgradePath(app.id));
                }}
              >
                {upgradePrice !== undefined ? `Upgrade for $${upgradePrice}/mo` : 'Upgrade'}
              </DsButton>
            ) : (
              <DsButton
                variant="primary"
                onClick={() => {
                  openApp();
                  onClose();
                }}
              >
                Open the app
              </DsButton>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default FirstDeployModal;
